import { EIP1193Provider } from 'eip1193-types';
// 为了方便客户代码使用，这里使用 ethers 包装了 eip1193
import { AbstractWalletConnector } from '../../core/AbstractWalletConnector';
import { ChainTypes } from '../../core/types';
import { ValueOrPromise, addListener, hexNumberString, isBrowser, isValidEVMAddress } from '../../misc';
import { ChainAddParam } from './evmChainInfo';
import { isInWeb3Mini, safeParseJSON } from '@web3/utils';

/**
 *  EIP 1193 Provider 或者和 ethers.provider.Web3Provider参数兼容的 Provider
 */
export class Web3ProviderConnector extends AbstractWalletConnector {
  private _provider?: EIP1193Provider;
  public get provider(): EIP1193Provider | undefined {
    return this._provider;
  }

  private _getEipProvider: () => Promise<EIP1193Provider | undefined>;

  /**
   *
   * @param provider provider在window对象上的变量名，例如 ethereum, bybitWallet, okxwallet等
   */
  constructor(name: string, provider: string);
  /**
   *
   * @param provider 直接的eip1193 provider对象，或者request函数，如果是函数将不具备通知功能了
   */
  constructor(name: string, provider: EIP1193Provider);
  /**
   *
   * @param provider 一个返回上面两种参数的函数
   */
  constructor(name: string, provider: () => ValueOrPromise<EIP1193Provider | string | undefined>);

  constructor(name: string, _provider: any) {
    super(name);
    this._getEipProvider = async () => {
      const provider = typeof _provider === 'function' ? await _provider() : _provider;
      if (!provider) {
        return;
      }
      if (typeof provider === 'string') {
        const eip1193Provider = isBrowser ? window[provider] : undefined;
        return eip1193Provider;
      }
      if (typeof provider === 'object') {
        return provider;
      }
      return;
    };
  }
  // 这个强标记chainType为 EVM
  readonly chainType = ChainTypes.EVM as const;

  private _unSubscribeEipProvider: (() => void) | undefined;
  private _subscribeEipProvider(provider: any) {
    this._unSubscribeEipProvider?.();

    if (!provider.on) {
      return;
    }
    const removeAccountChange = addListener(provider, 'accountsChanged', (accounts: string[] = []) => {
      let address = (accounts as string[] | undefined)?.[0];
      if (address && !isValidEVMAddress(address)) {
        address = undefined;
      }
      this.address = address;
    });
    const removeDisconnect = addListener(provider, 'disconnect', () => {
      this.address = undefined;
    });

    // 根据不同的钱包实际效果，有的是给 '0x1'(metamask) 有的是给 1 (walletconnect), 有的是给 对象(OkexWallet)
    const chainChange = (chain: string | number | { chainId: string | number }) => {
      const chainId = typeof chain === 'object' ? chain.chainId : chain;
      let numChainId = Number(chainId);
      if (isNaN(numChainId) && chainId && typeof chainId === 'string' && isInWeb3Mini()) {
        // 2024-09-30：bybit app web3 现在有一个bug，就是chainChanged 給过来的 chainId 字符串 是一个json，导致无法正常转成number
        // 这里做一个兼容，也是没办法。。。app已经在线上。。。而且app修复需要时间
        numChainId = Number(safeParseJSON(chainId));
      }
      this.chainId = numChainId;
    };

    const removeOnConnect = addListener(provider, 'connect', (x: number) => {
      chainChange(x);
    });
    const removeOnChainChanged = addListener(provider, 'chainChanged', (y: number) => {
      chainChange(y);
    });

    const unSubscribe = () => {
      removeAccountChange();
      removeOnConnect();
      removeOnChainChanged();
      removeDisconnect();
      this._unSubscribeEipProvider = undefined;
    };
    this._unSubscribeEipProvider = unSubscribe;
  }

  // 正常普通的插件钱包 provider connect 和 disconnect 默认不需要特殊处理
  async connect(recovery = false, ...args: any[]) {
    const provider = await this.getProvider();
    if (!provider) {
      throw new Error(`${this.name} provider not found or not ready`);
    }
    const accounts = await (recovery
      ? provider.request({ method: 'eth_accounts', params: [...args] })
      : provider.request({ method: 'eth_requestAccounts', params: [...args] }));
    const chainId = await provider.request({ method: 'eth_chainId', params: [] });

    const address = (accounts as string[] | undefined)?.[0];
    if (address) {
      if (!isValidEVMAddress(address)) {
        throw new Error(`Invalid address: ${address}`);
      }
    }
    this.address = address;
    this.chainId = Number(chainId);
    return this.address;
  }

  async disconnect() {
    this.address = undefined;
    this.publicKey = undefined;
    const provider = (await this.getProvider()) as any;
    if (typeof provider?.disconnect === 'function') {
      await provider?.disconnect();
    }
    return;
  }

  async getProvider(force = false): Promise<EIP1193Provider | undefined> {
    if (!this._provider || force) {
      const eipProvider = await this._getEipProvider();
      if (eipProvider) {
        this._provider = eipProvider;
        this._subscribeEipProvider(eipProvider);
      } else {
        this._provider = undefined;
      }
    }
    return this._provider;
  }

  async isAvailable() {
    const provider = await this.getProvider();
    return Boolean(provider);
  }

  async switchChain(chainId: string | number, chainInfo?: ChainAddParam) {
    const provider = await this.getProvider();
    if (provider) {
      return provider
        .request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: hexNumberString(chainId) }],
        })
        .catch((switchError) => {
          if (switchError.code === 4902 && chainInfo) {
            return this.addNetwork(chainInfo);
          } else {
            throw switchError;
          }
        });
    }
  }

  async addNetwork(network: ChainAddParam) {
    const provider = await this.getProvider();
    if (provider) {
      return provider.request({
        method: 'wallet_addEthereumChain',
        params: [network],
      });
    }
  }
}
